import { Row, Col, Card } from "antd";
import ChartTitle from "components/ChartTitle";
import LineChart from "components/charts/LineChart";
import Scorecard from "components/charts/Scorecard";
import { GRID_GUTTER } from "constants/ui";
import { DIMENSION_DATE } from "constants/dimensions";
import React, { useMemo } from "react";
import { isEmpty, sum } from "lodash";
import { useFormatNumber } from "hooks/useFormatNumber";
import { getDimensionColorByIndex } from "utils/colorUtils";
import { DATAKEY_USER_ACTIVATION } from "../../constants";

const ConversionRateSection = ({
  title,
  data,
  openDataKey,
  openMetricLabel,
  activationRateDataKey,
  activationDataKey = DATAKEY_USER_ACTIVATION,
  activationLabel,
  signupDataKey,
  signupLabel,
  signupRateDataKey,
}) => {
  const formatNumber = useFormatNumber();

  // Order matches scorecards: open, signup, activation (each keeps same color via stroke)
  const chartLines = useMemo(() => {
    const lines = [
      {
        name: openMetricLabel,
        dataKey: openDataKey,
        stroke: getDimensionColorByIndex(1),
      },
    ];
    if (signupDataKey) {
      lines.push({
        name: signupLabel || "User Signup",
        dataKey: signupDataKey,
        stroke: getDimensionColorByIndex(2),
      });
    }
    lines.push({
      name: activationLabel,
      dataKey: activationDataKey,
      stroke: getDimensionColorByIndex(0),
    });
    return lines;
  }, [
    activationDataKey,
    activationLabel,
    openDataKey,
    openMetricLabel,
    signupDataKey,
    signupLabel,
  ]);

  const totalOpen = useMemo(() => {
    if (isEmpty(data)) return 0;
    return sum(data.map((item) => item[openDataKey] || 0));
  }, [data, openDataKey]);

  const totalActivation = useMemo(() => {
    if (isEmpty(data)) return 0;
    return sum(data.map((item) => item[activationDataKey] || 0));
  }, [data, activationDataKey]);

  const totalSignup = useMemo(() => {
    if (!signupDataKey || isEmpty(data)) return 0;
    return sum(data.map((item) => item[signupDataKey] || 0));
  }, [data, signupDataKey]);

  const hasConversionData = useMemo(
    () =>
      Array.isArray(data) &&
      data.some(
        (item) =>
          (item[openDataKey] || 0) > 0 ||
          (item[activationDataKey] || 0) > 0 ||
          (signupDataKey && (item[signupDataKey] || 0) > 0)
      ),
    [data, openDataKey, activationDataKey, signupDataKey]
  );

  const chartData = hasConversionData ? data : [];

  const overallRate = useMemo(() => {
    if (totalOpen === 0) return "0";
    return ((totalActivation / totalOpen) * 100).toFixed(1);
  }, [totalActivation, totalOpen]);
  const signupRatePercent = useMemo(() => {
    if (!signupDataKey || totalOpen === 0) return "0";
    return ((totalSignup / totalOpen) * 100).toFixed(1);
  }, [signupDataKey, totalSignup, totalOpen]);

  return (
    <>
      <Col span={24}>
        <ChartTitle>{title}</ChartTitle>
      </Col>
      <Col xs={24} lg={12} className="mb-8">
        <Card size="small">
          <LineChart
            xAxisDataKey={DIMENSION_DATE}
            data={chartData}
            lines={chartLines}
          />
        </Card>
      </Col>
      {/* scorecards */}
      <Col xs={24} lg={12} className="mb-8">
        <Row gutter={GRID_GUTTER}>
          <Col span={24} className="mb-8">
            <Card size="small">
              <Scorecard
                title={openMetricLabel}
                value={formatNumber(totalOpen)}
                chartProps={{
                  data: chartData,
                  lines: [
                    {
                      name: openMetricLabel,
                      dataKey: openDataKey,
                      fill: getDimensionColorByIndex(1),
                    },
                  ],
                }}
              />
            </Card>
          </Col>
          <Col span={24} className="mb-8">
            <Card size="small">
              <Scorecard
                title={signupLabel}
                value={formatNumber(totalSignup)}
                chartProps={{
                  data: chartData,
                  lines: [
                    {
                      name: signupLabel,
                      dataKey: signupDataKey,
                      fill: getDimensionColorByIndex(2),
                    },
                  ],
                }}
              />
            </Card>
          </Col>
          <Col span={24} className="mb-8">
            <Card size="small">
              <Scorecard
                title={activationLabel}
                value={formatNumber(totalActivation)}
                chartProps={{
                  data: chartData,
                  lines: [
                    {
                      name: activationLabel,
                      dataKey: activationDataKey,
                      fill: getDimensionColorByIndex(0),
                    },
                  ],
                }}
              />
            </Card>
          </Col>

          {/* percentage rate cards: 2 columns */}
          <Col xs={24} md={12} className="mb-8">
            <Card size="small">
              <Scorecard
                title="Tooltip Signup Conversion Rate (Signups / Views)"
                value={`${signupRatePercent}%`}
                chartProps={{
                  data: chartData,
                  lines: signupRateDataKey
                    ? [
                        {
                          name: "Signup rate %",
                          dataKey: signupRateDataKey,
                          fill: getDimensionColorByIndex(3),
                        },
                      ]
                    : undefined,
                }}
              />
            </Card>
          </Col>
          <Col xs={24} md={12} className="mb-8">
            <Card size="small">
              <Scorecard
                title="Tooltip Activation Rate (Activations / Views)"
                value={`${overallRate}%`}
                chartProps={{
                  data: chartData,
                  lines: [
                    {
                      name: "Conversion rate %",
                      dataKey: activationRateDataKey,
                      fill: getDimensionColorByIndex(4),
                    },
                  ],
                }}
              />
            </Card>
          </Col>
        </Row>
      </Col>
    </>
  );
};

export default ConversionRateSection;
